import React, { useState, useEffect } from 'react';
import { BarChart3, LineChart as LineChartIcon, RefreshCw, AlertCircle } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';

/* Light premium trend card (spec: claude-code-incident-trend-spec.md).
   Real data from /analytics/incidents/timeseries; bars = grouped per severity. */

const SEVERITIES = [
  { key: 'high', label: 'High', color: '#dc2626' },
  { key: 'medium', label: 'Medium', color: '#ea580c' },
  { key: 'low', label: 'Low', color: '#eab308' },
];

const RANGE_CAPTION = { '24h': 'Last 24 hours · hourly', '7d': 'Last 7 days · daily', '30d': 'Last 30 days · daily' };

const CARD_STYLE = {
  background: 'linear-gradient(180deg,#fff,#fcfcff)',
  border: '1px solid #e6e9f4',
  borderRadius: 20,
  boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 14px 40px rgba(23,32,84,0.08)',
  padding: '22px 24px',
  fontFamily: "'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const bucketLabel = (ts, timeframe) => {
  const d = new Date(ts);
  if (timeframe === '24h') {
    let h = d.getHours();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}${ampm}`;
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const SegTrack = ({ children }) => (
  <div className="flex items-center gap-1" style={{ background: '#f1f3fa', border: '1px solid #e6e9f4', borderRadius: 10, padding: 4 }}>
    {children}
  </div>
);

const SegBtn = ({ active, onClick, children, testId, title }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    title={title}
    className="flex items-center justify-center rounded-lg text-[12.5px] font-bold transition-all"
    style={active
      ? { background: 'linear-gradient(135deg,#4338ca,#7c3aed)', color: '#fff', boxShadow: '0 4px 12px rgba(67,56,202,0.3)', padding: '5px 10px', border: 'none' }
      : { background: 'transparent', color: '#64748b', padding: '5px 10px', border: 'none' }}
  >
    {children}
  </button>
);

const DashboardIncidentTrendWidget = () => {
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('24h');
  const [chartType, setChartType] = useState('bar');
  const [hiddenSeverities, setHiddenSeverities] = useState(new Set());

  useEffect(() => {
    fetchTrendData();
  }, [timeframe]);

  const fetchTrendData = async () => {
    try {
      setLoading(true);
      setError(null);
      const now = new Date();
      const spans = { '24h': [24 * 3600e3, 'hour'], '7d': [7 * 86400e3, 'day'], '30d': [30 * 86400e3, 'day'] };
      const [span, bucket] = spans[timeframe] || spans['24h'];
      const params = new URLSearchParams({
        from_time: new Date(now.getTime() - span).toISOString(),
        to_time: now.toISOString(),
        bucket,
      });
      const response = await CommanServices.getanalyticsincidentstimeseries(params);
      if (response.status) setTrendData(response.data);
    } catch (err) {
      setError('Failed to load trend data');
      console.error('Trend data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSeverity = (severity) => {
    const next = new Set(hiddenSeverities);
    if (next.has(severity)) next.delete(severity); else next.add(severity);
    setHiddenSeverities(next);
  };

  const handleBucketClick = (bucket) => {
    const fromTime = new Date(bucket.ts);
    const toTime = new Date(fromTime.getTime() + (timeframe === '24h' ? 3600e3 : 86400e3));
    window.location.href = `/incidents?${new URLSearchParams({ from: fromTime.toISOString(), to: toTime.toISOString() })}`;
  };

  const header = (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <span
        className="flex h-10 w-10 items-center justify-center rounded-xl text-white"
        style={{ background: 'linear-gradient(135deg,#4338ca,#7c3aed)', boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 18px rgba(67,56,202,0.3)' }}
      >
        <BarChart3 className="h-5 w-5" />
      </span>
      <h3 className="text-[17px] font-bold" style={{ color: '#0f172a' }}>Incident Trend</h3>
      <div className="ml-auto flex items-center gap-2">
        <SegTrack>
          <SegBtn active={chartType === 'bar'} onClick={() => setChartType('bar')} testId="trend-mode-bar" title="Bar chart"><BarChart3 className="h-4 w-4" /></SegBtn>
          <SegBtn active={chartType === 'line'} onClick={() => setChartType('line')} testId="trend-mode-line" title="Line chart"><LineChartIcon className="h-4 w-4" /></SegBtn>
        </SegTrack>
        <SegTrack>
          {['24h', '7d', '30d'].map((r) => (
            <SegBtn key={r} active={timeframe === r} onClick={() => setTimeframe(r)} testId={`trend-range-${r}`}>{r}</SegBtn>
          ))}
        </SegTrack>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={CARD_STYLE} data-testid="incident-trend-widget">
        {header}
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={CARD_STYLE} data-testid="incident-trend-widget">
        {header}
        <div className="text-center py-6">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">{error}</p>
          <button onClick={fetchTrendData} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50">
            <RefreshCw className="h-3 w-3" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const points = trendData?.points || [];
  const hasData = points.some((p) => p.total > 0);
  const visible = SEVERITIES.filter((s) => !hiddenSeverities.has(s.key));
  const maxValue = Math.max(1, ...points.map((p) => Math.max(...visible.map((s) => p[s.key] || 0))));

  // SVG geometry
  const W = 760; const H = 240; const padL = 28; const padB = 24; const padT = 8;
  const plotW = W - padL - 8; const plotH = H - padT - padB;
  const n = Math.max(1, points.length);
  const slot = plotW / n;
  const gridVals = [0.25, 0.5, 0.75, 1].map((f) => Math.round(maxValue * f)).filter((v, i, a) => v > 0 && a.indexOf(v) === i);
  const yFor = (v) => padT + plotH - (v / maxValue) * plotH;

  return (
    <div style={CARD_STYLE} data-testid="incident-trend-widget">
      {header}
      {!hasData ? (
        <div className="text-center py-12" style={{ color: '#94a3b8' }}>
          <BarChart3 className="h-14 w-14 mx-auto mb-3 opacity-40" />
          <p className="text-base font-semibold" style={{ color: '#64748b' }}>No incidents in selected range</p>
          <p className="text-sm">Try adjusting the time period</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-4 mb-3">
            {SEVERITIES.map((s) => (
              <button
                key={s.key}
                onClick={() => toggleSeverity(s.key)}
                className="flex items-center gap-1.5 text-[12.5px] font-semibold transition-opacity"
                style={{ color: '#475569', opacity: hiddenSeverities.has(s.key) ? 0.4 : 1 }}
                data-testid={`trend-legend-${s.key}`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                {s.label}
              </button>
            ))}
            <span className="ml-auto text-[12px]" style={{ color: '#94a3b8' }}>{RANGE_CAPTION[timeframe]}</span>
          </div>

          <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minHeight: 220 }} data-testid="trend-chart">
            {gridVals.map((v) => (
              <g key={v}>
                <line x1={padL} x2={W - 8} y1={yFor(v)} y2={yFor(v)} stroke="#edf0f9" strokeWidth="1" />
                <text x={padL - 6} y={yFor(v) + 3} textAnchor="end" fontSize="10" fill="#a3abc4">{v}</text>
              </g>
            ))}
            {chartType === 'bar' ? (
              points.map((p, i) => {
                const groupW = visible.length * 9 - 2;
                const x0 = padL + i * slot + (slot - groupW) / 2;
                return (
                  <g key={i} onClick={() => handleBucketClick(p)} style={{ cursor: 'pointer' }}>
                    <title>{`${new Date(p.ts).toLocaleString()}: ${p.total} incidents`}</title>
                    <rect x={padL + i * slot} y={padT} width={slot} height={plotH} fill="transparent" />
                    {visible.map((s, j) => {
                      const v = p[s.key] || 0;
                      if (!v) return null;
                      const h = (v / maxValue) * plotH;
                      return <rect key={s.key} x={x0 + j * 9} y={padT + plotH - h} width={7} height={h} rx={2.5} fill={s.color} opacity={0.9} />;
                    })}
                  </g>
                );
              })
            ) : (
              visible.map((s) => {
                const pts = points.map((p, i) => `${padL + i * slot + slot / 2},${yFor(p[s.key] || 0)}`).join(' ');
                return (
                  <g key={s.key}>
                    <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                    {points.map((p, i) => (
                      <circle key={i} cx={padL + i * slot + slot / 2} cy={yFor(p[s.key] || 0)} r={3} fill="#fff" stroke={s.color} strokeWidth="2"
                        onClick={() => handleBucketClick(p)} style={{ cursor: 'pointer' }} />
                    ))}
                  </g>
                );
              })
            )}
            {points.map((p, i) => (
              i % 2 === 0 ? (
                <text key={i} x={padL + i * slot + slot / 2} y={H - 6} textAnchor="middle" fontSize="10.5" fill="#94a3b8">
                  {bucketLabel(p.ts, timeframe)}
                </text>
              ) : null
            ))}
          </svg>
        </>
      )}
    </div>
  );
};

export default DashboardIncidentTrendWidget;
